import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  structuredData?: Record<string, any>;
}

const SEO: React.FC<SEOProps> = ({
  title = 'travelbaba786 - Your Gateway to Global Travel Information',
  description = 'Access official visa requirements, ETA information, and travel documentation for every country. Verified links to government e-visa portals worldwide. Built for travelers, trusted by professionals.',
  keywords = 'travel, visa, ETA, travel requirements, visa information, e-visa, government portal, travel documentation, visa application, travel API, country visa, travel guide',
  image = 'https://travelbaba786.com/logo..PNG',
  url = 'https://travelbaba786.com',
  type = 'website',
  author = 'travelbaba786',
  publishedTime,
  modifiedTime,
  structuredData,
}) => {
  const fullTitle = title.includes('travelbaba786') ? title : `${title} | travelbaba786`;
  const fullUrl = url.startsWith('http') ? url : `https://travelbaba786.com${url}`;
  const fullImage = image.startsWith('http') ? image : `https://travelbaba786.com${image}`;

  // Default structured data if none provided
  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'travelbaba786',
    url: 'https://travelbaba786.com',
    description: description,
    publisher: {
      '@type': 'Organization',
      name: 'travelbaba786',
      url: 'https://travelbaba786.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://travelbaba786.com/logo..PNG',
      },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://travelbaba786.com/countries?search={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const schemaData = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <link rel="canonical" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="travelbaba786" />
      <meta property="og:locale" content="en_US" />
      {publishedTime && <meta property="article:published_time" content={publishedTime} />}
      {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:image:alt" content={title} />
      <meta name="twitter:creator" content="@travelersapi" />
      <meta name="twitter:site" content="@travelersapi" />

      {/* Additional Meta Tags */}
      <meta name="geo.region" content="US" />
      <meta name="geo.placename" content="United States" />
      <meta name="theme-color" content="#3b82f6" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="travelbaba786" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
};

export default SEO;

